import React, { useCallback, useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

interface PendingAdminRenewalRow {
  renewalRequestId: number;
  adminUserId: number;
  adminEmail: string | null;
  username: string | null;
  fromPlanName: string | null;
  toPlanName: string | null;
  amountPaid: number | string | null;
  currency: string | null;
  cryptoBlockchain: string | null;
  cryptoSymbol: string | null;
  cryptoDestinationAddress: string | null;
  cryptoTxHash: string | null;
  cryptoSubmittedAt: string | null;
  paymentMethod?: string | null;
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const AdminSubscriptionCryptoPaymentApprovals: React.FC = () => {
  const [rows, setRows] = useState<PendingAdminRenewalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);
  const [noteById, setNoteById] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.getPendingAdminSubscriptionCryptoVerifications();
      if (res.success && Array.isArray(res.data)) {
        setRows(res.data as PendingAdminRenewalRow[]);
      } else {
        setRows([]);
        setError(res.error || 'Failed to load pending admin crypto verifications');
      }
    } catch (e: any) {
      setRows([]);
      setError(e?.message || 'Failed to load pending admin crypto verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (renewalRequestId: number, approved: boolean) => {
    setActionId(renewalRequestId);
    setError(null);
    try {
      const note = (noteById[renewalRequestId] || '').trim() || undefined;
      const res = await adminApiService.verifyAdminSubscriptionCryptoPayment({
        renewalRequestId,
        approved,
        note,
      });
      if (!res.success) {
        setError(res.error || (approved ? 'Approve failed' : 'Reject failed'));
        return;
      }
      setNoteById((prev) => {
        const next = { ...prev };
        delete next[renewalRequestId];
        return next;
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Verification failed');
    } finally {
      setActionId(null);
    }
  };

  const cancelPending = async (renewalRequestId: number) => {
    const ok = window.confirm('Delete/cancel this unfinished admin subscription flow?');
    if (!ok) return;
    setActionId(renewalRequestId);
    setError(null);
    try {
      const note = (noteById[renewalRequestId] || '').trim() || undefined;
      const res = await adminApiService.cancelAdminSubscriptionCryptoOrPaymentFlow({
        renewalRequestId,
        note,
      });
      if (!res.success) {
        setError(res.error || 'Delete pending flow failed');
        return;
      }
      setNoteById((prev) => {
        const next = { ...prev };
        delete next[renewalRequestId];
        return next;
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Delete pending flow failed');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="admin-section" style={{ padding: '0 4px' }}>
      <h2 style={{ margin: 0, marginBottom: 8, fontSize: 22 }}>Admin renewal crypto approvals</h2>
      <p style={{ margin: '0 0 14px', color: '#6b7280', fontSize: 14 }}>
        Review open admin subscription payment registrations. You can approve/reject submitted crypto proofs or delete unfinished flows.
      </p>
      {error && <div style={{ marginBottom: 12, color: '#b91c1c', fontSize: 13 }}>{error}</div>}
      {loading && rows.length === 0 ? (
        <div style={{ color: '#6b7280' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#6b7280' }}>No pending admin crypto verifications.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Admin</th>
                <th style={{ padding: '10px 12px' }}>Plan</th>
                <th style={{ padding: '10px 12px' }}>Amount</th>
                <th style={{ padding: '10px 12px' }}>Chain / Symbol</th>
                <th style={{ padding: '10px 12px' }}>Tx hash</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Note</th>
                <th style={{ padding: '10px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.renewalRequestId} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 700 }}>{r.adminEmail || r.username || `Admin ${r.adminUserId}`}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Request #{r.renewalRequestId}</div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>{(r.fromPlanName || '-') + ' → ' + (r.toPlanName || '-')}</td>
                  <td style={{ padding: '10px 12px' }}>{r.amountPaid ?? '-'} {r.currency || ''}</td>
                  <td style={{ padding: '10px 12px' }}>{(r.cryptoBlockchain || '-') + ' / ' + (r.cryptoSymbol || '-')}</td>
                  <td style={{ padding: '10px 12px', maxWidth: 220, wordBreak: 'break-all' }}>{r.cryptoTxHash || '-'}</td>
                  <td style={{ padding: '10px 12px' }}>{r.status || '-'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <input
                      type="text"
                      value={noteById[r.renewalRequestId] || ''}
                      onChange={(e) => setNoteById((prev) => ({ ...prev, [r.renewalRequestId]: e.target.value }))}
                      placeholder="Optional note"
                      style={{ width: 170, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {r.status === 'PENDING_CRYPTO_VERIFICATION' ? (
                        <>
                          <button
                            type="button"
                            disabled={actionId === r.renewalRequestId}
                            onClick={() => void submit(r.renewalRequestId, true)}
                            style={{ padding: '6px 10px', borderRadius: 999, border: 'none', background: '#0dca9d', color: '#fff' }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={actionId === r.renewalRequestId}
                            onClick={() => void submit(r.renewalRequestId, false)}
                            style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #ff7171', background: '#fff', color: '#b91c1c' }}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        disabled={actionId === r.renewalRequestId}
                        onClick={() => void cancelPending(r.renewalRequestId)}
                        style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #f59e0b', background: '#fff7ed', color: '#9a3412' }}
                      >
                        Delete pending flow
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionCryptoPaymentApprovals;
